// Browser implementation of `VoicePlaybackDriver` (shared layer lives
// in packages/ui/src/chat/composer/voicePlaybackDriver.ts). Wraps the
// HTMLAudioElement for actual playback and `AudioContext.decodeAudioData`
// for the bubble waveform. All Blob URL bookkeeping is local to this
// module — the shared bubble never sees a browser-only primitive.
//
// Source shapes:
//   `web-blob`     — pre-resolved `blob:` URL (sender-side echo path).
//   `macos-base64` — base64 + mime (interoperable cross-platform path).
//                    Decoded to a Blob here so a single render path
//                    handles both kinds.

import {
  deterministicWaveform,
  VOICE_WAVEFORM_BUCKETS,
  type VoicePlaybackDriver,
  type VoicePlaybackHandle,
  type VoiceSource,
} from '@ping/ui';

export function createWebVoicePlaybackDriver(): VoicePlaybackDriver {
  // The driver is stateless across calls — each `play()` resolves its
  // own `<audio>` element and the returned handle owns its lifecycle.
  // Waveform decodes share a singleton AudioContext (cheap to allocate
  // but lots of warnings if you call `new AudioContext()` repeatedly).
  let ctx: AudioContext | null = null;
  const getCtx = (): AudioContext | null => {
    if (ctx !== null) return ctx;
    const W = window as unknown as {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    };
    const Ctor = W.AudioContext ?? W.webkitAudioContext;
    if (Ctor === undefined) return null;
    try {
      ctx = new Ctor();
      return ctx;
    } catch {
      return null;
    }
  };

  return {
    // NOTE: `play` resolves a handle SYNCHRONOUSLY-as-possible and starts
    // `audio.play()` inside the same task as the caller's click. Anything
    // that yields to a macrotask (a `setTimeout`, a slow `await`) before
    // `play()` drops the browser's user-activation and the autoplay policy
    // silently refuses to start — the prior cause of "toggles but no sound".
    // We resolve the blob URL synchronously and call `play()` immediately.
    play({ source, onProgress, onEnded, onDuration, rate, onError }): Promise<VoicePlaybackHandle> {
      const playableUrl = resolvePlayableUrl(source);
      const audio = new Audio();
      audio.preload = 'auto';
      audio.volume = 1;
      // Track the requested rate in a closure. Loading a new `src` runs the
      // media-load algorithm, which resets `playbackRate` to
      // `defaultPlaybackRate` — so we set BOTH and re-apply on the load
      // events below, otherwise the rate silently snaps back to 1× and the
      // speed pill appears to do nothing.
      let cleaned = false;
      let currentRate = typeof rate === 'number' && rate > 0 ? rate : 1;
      const applyRate = (): void => {
        if (cleaned) return;
        audio.defaultPlaybackRate = currentRate;
        audio.playbackRate = currentRate;
      };
      // Keep the element in the DOM (visually hidden). A purely detached
      // `new Audio()` can resolve `play()` yet never advance `currentTime`
      // (no sound, no progress) in some engines.
      audio.setAttribute('aria-hidden', 'true');
      audio.style.cssText =
        'position:absolute;width:0;height:0;opacity:0;pointer-events:none;left:-9999px;';
      if (typeof document !== 'undefined' && document.body !== null) {
        document.body.appendChild(audio);
      }
      audio.src = playableUrl;
      applyRate();

      let rafId = 0;

      const reportDuration = (): void => {
        const d = audio.duration;
        if (onDuration !== undefined && Number.isFinite(d) && d > 0) {
          onDuration(Math.round(d * 1000));
        }
      };
      audio.addEventListener('loadedmetadata', reportDuration);
      audio.addEventListener('durationchange', reportDuration);
      // Re-assert the rate after every (re)load milestone — the media-load
      // algorithm clears `playbackRate` back to 1× as the resource loads.
      audio.addEventListener('loadedmetadata', applyRate);
      audio.addEventListener('durationchange', applyRate);
      audio.addEventListener('canplay', applyRate);
      audio.addEventListener('playing', applyRate);
      audio.addEventListener('ratechange', () => {
        // Some engines reset the rate to 1× while a webm with late
        // duration metadata is still resolving; pin it back if it drifted
        // away from what the user picked (guarding against a feedback loop
        // by only correcting a real mismatch).
        if (!cleaned && audio.playbackRate !== currentRate) applyRate();
      });

      const cleanup = (): void => {
        if (cleaned) return;
        cleaned = true;
        if (rafId !== 0) cancelAnimationFrame(rafId);
        try {
          audio.pause();
        } catch {
          /* best-effort */
        }
        audio.removeAttribute('src');
        try {
          audio.load();
        } catch {
          /* best-effort — GC the decoder */
        }
        try {
          audio.remove();
        } catch {
          /* best-effort */
        }
        if (source.kind === 'macos-base64') {
          try {
            URL.revokeObjectURL(playableUrl);
          } catch {
            /* best-effort */
          }
        }
      };

      // Smooth (60fps) progress via requestAnimationFrame — `timeupdate`
      // only fires ~4Hz, which reads as a janky waveform fill. The rAF
      // loop self-stops when the element is paused/ended.
      const tick = (): void => {
        if (cleaned) return;
        onProgress(Math.round(audio.currentTime * 1000));
        if (!audio.paused && !audio.ended) {
          rafId = requestAnimationFrame(tick);
        }
      };
      audio.addEventListener('playing', () => {
        if (rafId !== 0) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(tick);
      });
      audio.addEventListener('timeupdate', () => {
        // Backup for engines where rAF is throttled while hidden.
        onProgress(Math.round(audio.currentTime * 1000));
      });
      audio.addEventListener('ended', () => {
        try {
          onEnded();
        } finally {
          cleanup();
        }
      });
      // A media element fires `error` INSTEAD of rejecting `play()` for an
      // undecodable codec/container. Treat it like "ended" so the bubble
      // resets to idle, and surface it.
      audio.addEventListener('error', () => {
        const code = audio.error?.code ?? '?';
        try {
          onError?.(new Error(`audio decode failed (code ${code})`));
        } finally {
          try {
            onEnded();
          } finally {
            cleanup();
          }
        }
      });

      // Fire-and-forget play within the gesture. We do NOT await it before
      // returning the handle, so a slow/stalled decode can never leave the
      // bubble stuck "playing" with no way to pause.
      void audio
        .play()
        .catch((err: unknown) => {
          // A late stop() (component teardown) can abort an in-flight
          // play() with AbortError — that's expected, not a failure.
          if ((err as Error)?.name === 'AbortError' || cleaned) return;
          // Autoplay refusal or decode failure — surface + reset to idle.
          try {
            onError?.(err);
          } finally {
            try {
              onEnded();
            } finally {
              cleanup();
            }
          }
        });

      return Promise.resolve({
        stop: cleanup,
        setRate: (r: number) => {
          if (r > 0 && !cleaned) {
            currentRate = r;
            applyRate();
          }
        },
      });
    },

    async extractWaveform(source: VoiceSource, seed?: string): Promise<number[]> {
      const fallback = (): number[] =>
        deterministicWaveform(seed ?? 'voice', VOICE_WAVEFORM_BUCKETS);
      const ac = getCtx();
      if (ac === null) return fallback();
      try {
        const playableUrl = resolvePlayableUrl(source);
        const res = await fetch(playableUrl);
        const buf = await res.arrayBuffer();
        // `decodeAudioData` mutates the buffer; pass a copy so we can
        // also revoke the ephemeral URL safely after.
        const copy = buf.slice(0);
        const audioBuffer = await ac.decodeAudioData(copy);
        if (source.kind === 'macos-base64') {
          try {
            URL.revokeObjectURL(playableUrl);
          } catch {
            // Best-effort.
          }
        }
        const samples = audioBuffer.getChannelData(0);
        const bucketCount = VOICE_WAVEFORM_BUCKETS;
        const bucketSize = Math.max(1, Math.floor(samples.length / bucketCount));
        const buckets = new Array<number>(bucketCount).fill(0);
        let peak = 0;
        for (let b = 0; b < bucketCount; b++) {
          const start = b * bucketSize;
          const end = Math.min(samples.length, start + bucketSize);
          let sumSq = 0;
          for (let i = start; i < end; i++) {
            const v = samples[i] ?? 0;
            sumSq += v * v;
          }
          const count = Math.max(1, end - start);
          const rms = Math.sqrt(sumSq / count);
          buckets[b] = rms;
          if (rms > peak) peak = rms;
        }
        if (peak === 0) return fallback();
        // Normalise to 0..1 with a small floor so silent buckets still
        // render as visible bars.
        return buckets.map((v) => Math.max(0.08, v / peak));
      } catch {
        return fallback();
      }
    },
  };
}

// Synchronous so `play()` can call `audio.play()` within the click's
// user-activation window (an `await` here would drop the gesture).
function resolvePlayableUrl(source: VoiceSource): string {
  if (source.kind === 'web-blob') return source.blobUrl;
  // `macos-base64` — decode to a Blob and create an ephemeral object
  // URL. Caller cleans it up via `URL.revokeObjectURL` after use.
  const bytes = base64ToBytes(source.base64);
  const ab = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(ab).set(bytes);
  const blob = new Blob([ab], { type: source.mime });
  return URL.createObjectURL(blob);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const len = binary.length;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}
